import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  alpha,
  Container,
  Paper,
  Pagination,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import SummarizeIcon from '@mui/icons-material/Summarize';
import RefreshIcon from '@mui/icons-material/Refresh';
import CachedIcon from '@mui/icons-material/Cached';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { motion } from 'framer-motion';
import ContentLayout from '../components/common/ContentLayout';
import { downloadNationalSummaryPDF, downloadGlobalSummaryPDF } from '../utils/summaryPdf';
import {
  getFilterCountries,
  getFilterProvinces,
  getFilterMunicipalities,
  getForecastScores,
  getForecastStats,
  getForecastGlobalStats,
  getResilienceFactors,
  updateForecastScores,
  getAIRecommendation,
  getCountrySummary,
  getGlobalSummary
} from '../services/impactService';

const SCORE_OPTIONS = [1, 2, 3, 4];

// Helper: treat only 1–4 as valid forecast scores
const isValidForecastScore = (value) =>
  typeof value === 'number' && SCORE_OPTIONS.includes(value);

const ImpactForecasting = ({
  embedded = false,
  initialCountryId = '',
  initialProvinceId = '',
  initialMunicipalityId = '',
}) => {
  const theme = useTheme();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [sites, setSites] = useState([]);
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSitesCount, setTotalSitesCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [hasFetched, setHasFetched] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [currentAIContext, setCurrentAIContext] = useState({ siteId: null, factorId: null, siteName: '', factorName: '' });
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [countrySummary, setCountrySummary] = useState(null);
  const [expandedCards, setExpandedCards] = useState({}); // Track which MA cards are expanded
  const [countryStats, setCountryStats] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [globalStatsLoading, setGlobalStatsLoading] = useState(false);
  const [globalSummaryModalOpen, setGlobalSummaryModalOpen] = useState(false);
  const [globalSummaryLoading, setGlobalSummaryLoading] = useState(false);
  const [globalSummary, setGlobalSummary] = useState(null);

  // Toggle card expansion
  const toggleCardExpansion = (siteId) => {
    setExpandedCards(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  // Get summary of goals for a site (for collapsed view)
  const getSiteGoalsSummary = (site) => {
    if (!site.factorScores || site.factorScores.length === 0) {
      return {
        avgBaseline: null, avgBaselinePercentage: null,
        avgScore: null, avgPercentage: null,
        avg5yr: null, avg5yrPercentage: null,
        avg10yr: null, avg10yrPercentage: null,
        factors: []
      };
    }
    
    const factorsWithGoals = site.factorScores.filter(score => 
      isValidForecastScore(score.score5yrs) || isValidForecastScore(score.score10yrs)
    );

    // Baseline average (snapshot at end of FY2025 from impact_history)
    const validBaselineScores = site.factorScores.filter(score =>
      typeof score.baselineScore === 'number' && score.baselineScore > 0
    );
    const avgBaseline = validBaselineScores.length > 0
      ? validBaselineScores.reduce((sum, s) => sum + s.baselineScore, 0) / validBaselineScores.length
      : null;
    const avgBaselinePercentage = avgBaseline !== null ? ((avgBaseline / 4) * 100).toFixed(0) : null;

    // Calculate average current score (only for factors with valid scores)
    const validCurrentScores = site.factorScores.filter(score => 
      typeof score.currentScore === 'number' && score.currentScore > 0
    );
    const avgScore = validCurrentScores.length > 0 
      ? validCurrentScores.reduce((sum, s) => sum + s.currentScore, 0) / validCurrentScores.length
      : null;
    const avgPercentage = avgScore !== null ? ((avgScore / 4) * 100).toFixed(0) : null;
    
    // Calculate average 5yr goal score
    const valid5yrScores = site.factorScores.filter(score => isValidForecastScore(score.score5yrs));
    const avg5yr = valid5yrScores.length > 0 
      ? valid5yrScores.reduce((sum, s) => sum + s.score5yrs, 0) / valid5yrScores.length
      : null;
    const avg5yrPercentage = avg5yr !== null ? ((avg5yr / 4) * 100).toFixed(0) : null;
    
    // Calculate average 10yr goal score
    const valid10yrScores = site.factorScores.filter(score => isValidForecastScore(score.score10yrs));
    const avg10yr = valid10yrScores.length > 0 
      ? valid10yrScores.reduce((sum, s) => sum + s.score10yrs, 0) / valid10yrScores.length
      : null;
    const avg10yrPercentage = avg10yr !== null ? ((avg10yr / 4) * 100).toFixed(0) : null;
    
    return {
      avgBaseline: avgBaseline !== null ? Math.round(avgBaseline * 10) / 10 : null,
      avgBaselinePercentage,
      avgScore: avgScore !== null ? Math.round(avgScore * 10) / 10 : null,
      avgPercentage,
      avg5yr: avg5yr !== null ? Math.round(avg5yr * 10) / 10 : null,
      avg5yrPercentage,
      avg10yr: avg10yr !== null ? Math.round(avg10yr * 10) / 10 : null,
      avg10yrPercentage,
      factors: factorsWithGoals.map(score => {
        const factor = factors.find(f => f.id === score.factorId);
        return {
          name: factor?.name || 'Unknown',
          current: score.currentScore,
          goal5yr: isValidForecastScore(score.score5yrs) ? score.score5yrs : null,
          goal10yr: isValidForecastScore(score.score10yrs) ? score.score10yrs : null
        };
      })
    };
  };

  // Check if any sites have valid forecasted scores (5yr or 10yr goals 1–4)
  const hasForecastedScores = sites.some(site => 
    site.factorScores?.some(score => 
      isValidForecastScore(score.score5yrs) || isValidForecastScore(score.score10yrs)
    )
  );

  // Calculate forecasting statistics
  const forecastStats = countryStats || {
    totalSites: totalSitesCount || 0,
    sitesWithGoals: 0,
    sitesWithoutGoals: 0,
    totalPossibleGoals: 0,
    totalPossible5yr: 0,
    totalPossible10yr: 0,
    goals5yrSet: 0,
    goals10yrSet: 0,
    totalGoalsSet: 0,
    goalsRemaining: 0,
    completionPercentage: 0,
    byFactor: {}
  };

  // Initialize from parent (e.g., ProgramImpact modal)
  useEffect(() => {
    if (initialCountryId) setSelectedCountry(initialCountryId);
    if (initialProvinceId) setSelectedProvince(initialProvinceId);
    if (initialMunicipalityId) setSelectedMunicipality(initialMunicipalityId);
  // we want this to run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch countries and factors on initial load
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [countriesData, factorsData] = await Promise.all([
          getFilterCountries(),
          getResilienceFactors()
        ]);
        setCountries(countriesData);
        setFactors(factorsData);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading initial data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch global stats when no country is selected
  useEffect(() => {
    const fetchGlobalStats = async () => {
      if (!selectedCountry) {
        try {
          setGlobalStatsLoading(true);
          const stats = await getForecastGlobalStats();
          setGlobalStats(stats);
        } catch (error) {
          console.error('Error fetching global stats:', error);
          // Don't show error snackbar for global stats - it's optional
        } finally {
          setGlobalStatsLoading(false);
        }
      } else {
        // Clear global stats when a country is selected
        setGlobalStats(null);
      }
    };
    fetchGlobalStats();
  }, [selectedCountry]);

  // Modify the fetch functions to handle "all" selections
  const fetchProvinces = async (countryId) => {
    try {
      const data = await getFilterProvinces(countryId);
      setProvinces(data);
    } catch (error) {
      console.error('Error fetching provinces:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch provinces',
        severity: 'error'
      });
    }
  };

  const fetchMunicipalities = async (provinceId) => {
    try {
      const data = await getFilterMunicipalities(provinceId);
      setMunicipalities(data);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch municipalities',
        severity: 'error'
      });
    }
  };

  const fetchSites = async () => {
    if (!selectedCountry) return;
    
    setLoading(true);
    try {
      let params = { 
        countryId: selectedCountry,
        page,
        per_page: 10
      };
      
      if (selectedProvince && selectedProvince !== 'all') {
        params.provinceId = selectedProvince;
      }
      if (selectedMunicipality && selectedMunicipality !== 'all') {
        params.municipalityId = selectedMunicipality;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      
      const [response, statsResponse, factorsData] = await Promise.all([
        getForecastScores(params),
        getForecastStats({
          countryId: selectedCountry,
          ...(selectedProvince && selectedProvince !== 'all' ? { provinceId: selectedProvince } : {}),
          ...(selectedMunicipality && selectedMunicipality !== 'all' ? { municipalityId: selectedMunicipality } : {}),
          ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {})
        }),
        getResilienceFactors()
      ]);
      
      setSites(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalSitesCount(response.pagination.total || 0);
      setCountryStats(statsResponse);
      setFactors(factorsData); // Ensure factors are always up to date
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch sites',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    if (selectedCountry) {
      fetchProvinces(selectedCountry);
      setSelectedProvince('');
      setSelectedMunicipality('');
      fetchSites();
    } else {
      setCountryStats(null);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedProvince) {
      if (selectedProvince === 'all') {
        setMunicipalities([]);
        setSelectedMunicipality('');
      } else {
        fetchMunicipalities(selectedProvince);
        setSelectedMunicipality('');
      }
      fetchSites();
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (selectedMunicipality) {
      fetchSites();
    }
  }, [selectedMunicipality]);

  // Fetch sites when search term, page, or filters change
  useEffect(() => {
    if (selectedCountry) {
      fetchSites();
    }
  }, [debouncedSearchTerm, page]);

  const handleScoreChange = (siteId, factorId, field, value) => {
    setSites(prevSites => {
      return prevSites.map(site => {
        if (site.siteId === siteId) {
          return {
            ...site,
            factorScores: site.factorScores.map(score => {
              if (score.factorId === factorId) {
                return { ...score, [field]: value };
              }
              return score;
            })
          };
        }
        return site;
      });
    });
  };

  const handleSave = async (site) => {
    try {
      await updateForecastScores(site.siteId, site.factorScores);
      setSnackbar({
        open: true,
        message: 'Forecast scores saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving forecast:', error);
      setSnackbar({
        open: true,
        message: 'Error saving forecast. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleAIRecommendation = async (siteId, factorId, siteName, factorName) => {
    const key = `${siteId}-${factorId}`;
    setAiLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await getAIRecommendation(siteId, factorId);
      
      // Open modal with AI recommendation instead of auto-filling
      setAiRecommendation(response.recommendation);
      setCurrentAIContext({ siteId, factorId, siteName, factorName });
      setAiModalOpen(true);
      
      setSnackbar({
        open: true,
        message: 'AI recommendations generated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error getting AI recommendation:', error);
      setSnackbar({
        open: true,
        message: 'Error generating AI recommendations. Please try again.',
        severity: 'error'
      });
    } finally {
      setAiLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleCopyToComments = () => {
    const { siteId, factorId } = currentAIContext;
    setSites(prevSites => {
      return prevSites.map(site => {
        if (site.siteId === siteId) {
          return {
            ...site,
            factorScores: site.factorScores.map(score => {
              if (score.factorId === factorId) {
                return { ...score, comments: aiRecommendation };
              }
              return score;
            })
          };
        }
        return site;
      });
    });
    setAiModalOpen(false);
    setSnackbar({
      open: true,
      message: 'AI recommendations copied to comments',
      severity: 'success'
    });
  };

  const handleCountrySummary = async (forceRegenerate = false) => {
    if (!selectedCountry) {
      setSnackbar({
        open: true,
        message: 'Please select a country first',
        severity: 'warning'
      });
      return;
    }
    
    setSummaryLoading(true);
    setSummaryModalOpen(true);
    
    try {
      const response = await getCountrySummary(selectedCountry, forceRegenerate);
      setCountrySummary(response);
    } catch (error) {
      console.error('Error generating country summary:', error);
      setSnackbar({
        open: true,
        message: 'Error generating country summary. Please try again.',
        severity: 'error'
      });
      setSummaryModalOpen(false);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleGlobalSummary = async (forceRegenerate = false) => {
    setGlobalSummaryLoading(true);
    setGlobalSummaryModalOpen(true);
    
    try {
      const response = await getGlobalSummary(forceRegenerate);
      setGlobalSummary(response);
    } catch (error) {
      console.error('Error generating global summary:', error);
      setSnackbar({
        open: true,
        message: 'Error generating global summary. Please try again.',
        severity: 'error'
      });
      setGlobalSummaryModalOpen(false);
    } finally {
      setGlobalSummaryLoading(false);
    }
  };

  // Enhanced markdown parser for AI recommendations
  const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null;

    // Helper to clean markdown formatting from text
    const cleanMarkdown = (str) => {
      return str
        .replace(/\*\*(.+?)\*\*/g, '$1')  // Remove bold **text**
        .replace(/\*(.+?)\*/g, '$1')       // Remove italic *text*
        .replace(/__(.+?)__/g, '$1')       // Remove bold __text__
        .replace(/_(.+?)_/g, '$1')         // Remove italic _text_
        .replace(/`(.+?)`/g, '$1')         // Remove inline code `text`
        .trim();
    };

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ul') {
          elements.push(
            <Box component="ul" key={`list-${elements.length}`} sx={{ my: 1, pl: 3 }}>
              {listItems.map((item, idx) => (
                <Typography component="li" key={idx} sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  {item}
                </Typography>
              ))}
            </Box>
          );
        } else if (listType === 'ol') {
          elements.push(
            <Box component="ol" key={`list-${elements.length}`} sx={{ my: 1, pl: 3 }}>
              {listItems.map((item, idx) => (
                <Typography component="li" key={idx} sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  {item}
                </Typography>
              ))}
            </Box>
          );
        }
        listItems = [];
        listType = null;
      }
    };

    lines.forEach((line, idx) => {
      // Check for headers (# ## ### or **Header**)
      const h1Match = line.match(/^#\s+(.+)$/);
      const h2Match = line.match(/^##\s+(.+)$/);
      const h3Match = line.match(/^###\s+(.+)$/);
      const boldHeaderMatch = line.match(/^\*\*([^*]+)\*\*:?\s*$/);
      
      if (h1Match || h2Match || h3Match || boldHeaderMatch) {
        flushList();
        const headerText = cleanMarkdown(h1Match?.[1] || h2Match?.[1] || h3Match?.[1] || boldHeaderMatch?.[1]);
        const isH1 = h1Match;
        elements.push(
          <Typography 
            key={`header-${idx}`} 
            sx={{ 
              fontWeight: 700, 
              fontSize: isH1 ? '1rem' : '0.9rem',
              mt: elements.length > 0 ? 2 : 0, 
              mb: 1,
              color: 'primary.main'
            }}
          >
            {headerText}
          </Typography>
        );
        return;
      }

      // Check for bullet points (-, *, •)
      const bulletMatch = line.match(/^[\s]*[-*•]\s+(.+)$/);
      if (bulletMatch) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(cleanMarkdown(bulletMatch[1]));
        return;
      }

      // Check for numbered lists
      const numberedMatch = line.match(/^[\s]*\d+\.\s+(.+)$/);
      if (numberedMatch) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(cleanMarkdown(numberedMatch[1]));
        return;
      }

      // Not a list item, flush any pending list
      flushList();

      // Regular paragraph - also handle lines that start with bold (like "**Key:** value")
      const cleanLine = cleanMarkdown(line);
      if (cleanLine) {
        elements.push(
          <Typography key={`para-${idx}`} sx={{ mb: 1, fontSize: '0.85rem', lineHeight: 1.6 }}>
            {cleanLine}
          </Typography>
        );
      } else if (elements.length > 0) {
        // Add spacing for empty lines
        elements.push(<Box key={`space-${idx}`} sx={{ height: 8 }} />);
      }
    });

    // Flush any remaining list
    flushList();

    return elements;
  };

  const handleSearchClear = () => {
    setSearchTerm('');
  };

  const getScoreChipStyle = (score) => {
    if (score === null || score === undefined || isNaN(score)) {
      return {
        backgroundColor: alpha(theme.palette.grey[300], 0.3),
        color: theme.palette.text.secondary,
        fontWeight: 500,
      };
    }
    
    let color;
    if (score <= 1.5) {
      color = '#FF0000'; // Red for low scores
    } else if (score <= 2.5) {
      color = '#FFC000'; // Orange for developing scores
    } else if (score <= 3.5) {
      color = '#92D050'; // Light green for established scores
    } else {
      color = '#00B050'; // Dark green for advanced scores
    }

    return {
      backgroundColor: color,
      color: score <= 1.5 || score >= 3.5 ? 'white' : 'black',
      fontWeight: 'bold',
    };
  };

  const content = (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Title and Description Section */}
        {!embedded && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
          <Typography 
            variant="h2" 
            component="h1"
            sx={{
                  background: 'linear-gradient(135deg, #1A3E5F 0%, #2F6C75 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 2
            }}
          >
            Impact Forecasting Tool
          </Typography>
              <Typography variant="h6" color="text.secondary">
            Project future resilience scores and track predicted changes over time
          </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {/* Global Summary button - always visible */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={globalSummaryLoading ? <CircularProgress size={18} color="inherit" /> : <SummarizeIcon />}
                onClick={() => handleGlobalSummary(false)}
                disabled={globalSummaryLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 91, 187, 0.04)',
                    borderWidth: 1,
                    borderColor: 'divider',
                    transform: 'none',
                    boxShadow: 'none'
                  },
                  '&:active': { transform: 'none' },
                  '&:disabled': {
                    borderColor: 'divider',
                    color: 'text.disabled'
                  }
                }}
              >
                {globalSummaryLoading ? 'Generating...' : 'Global Summary'}
              </Button>

              {/* National Summary button - only when country selected */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={summaryLoading ? <CircularProgress size={18} color="inherit" /> : <SummarizeIcon />}
                onClick={() => handleCountrySummary(false)}
                disabled={!selectedCountry || summaryLoading || loading || sites.length === 0 || !hasForecastedScores}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 91, 187, 0.04)',
                    borderWidth: 1,
                    borderColor: 'divider',
                    transform: 'none',
                    boxShadow: 'none'
                  },
                  '&:active': { transform: 'none' },
                  '&:disabled': {
                    borderColor: 'divider',
                    color: 'text.disabled'
                  }
                }}
              >
                {summaryLoading ? 'Generating...' : 'National Summary'}
              </Button>
            </Box>
          </Box>
        </Box>
        )}

        {/* Filters Section */}
        <Paper 
          sx={{ 
            p: 3,
            mb: embedded ? 2 : 4,
            width: '100%',
            border: '1px solid #CBD5E1',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ mb: 0 }}>
              Forecasting Filters
            </Typography>
          </Box>
          <Grid container spacing={embedded ? 1.5 : 2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Select Country"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem',
                        py: 1
                      },
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                      All Countries
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem 
                        key={country.id} 
                        value={country.id}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Select Province"
                    value={selectedProvince}
                    onChange={(e) => setSelectedProvince(e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={!selectedCountry}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem',
                        py: 1
                      },
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                      All Provinces
                    </MenuItem>
                    {provinces.map((province) => (
                      <MenuItem 
                        key={province.id} 
                        value={province.id}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {province.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Select Municipality"
                    value={selectedMunicipality}
                    onChange={(e) => setSelectedMunicipality(e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={!selectedProvince}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem',
                        py: 1
                      },
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                      All Municipalities
                    </MenuItem>
                    {municipalities.map((municipality) => (
                      <MenuItem 
                        key={municipality.id} 
                        value={municipality.id}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {municipality.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Search Sites"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: '1rem' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search"
                            onClick={handleSearchClear}
                            edge="end"
                            size="small"
                          >
                            <ClearIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
        </Paper>

        {/* Global Forecasting Statistics (shown when no country selected) */}
        {!loading && !selectedCountry && globalStats && (
          <Paper
            sx={{
              p: 2.5,
              mb: embedded ? 2 : 4,
              width: '100%',
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Global Forecasting Progress
              </Typography>
              <Chip label="All Countries" size="small" sx={{ fontSize: '0.7rem', height: 22, backgroundColor: alpha(theme.palette.info.main, 0.1), color: 'info.dark' }} />
            </Box>

            {globalStatsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
          </Box>
            ) : (
              <Grid container spacing={2.5} alignItems="flex-start">
                {/* Left: 2×2 stat cards + completion bar */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                      Overview
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{
                        borderRadius: '10px',
                        border: `1px solid ${alpha('#1A3E5F', 0.18)}`,
                        backgroundColor: alpha('#1A3E5F', 0.04),
                        boxShadow: 'none',
                      }}>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                            Total Sites
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: '#1A3E5F' }}>
                            {globalStats.totalSites}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{
                        borderRadius: '10px',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.04),
                        boxShadow: 'none',
                      }}>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                            Sites With Goals
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: 'success.main' }}>
                            {globalStats.sitesWithGoals}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                              / {globalStats.totalSites}
                            </Typography>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{
                        borderRadius: '10px',
                        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                        backgroundColor: alpha(theme.palette.info.main, 0.04),
                        boxShadow: 'none',
                      }}>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                            Goals Set
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: 'info.dark' }}>
                            {globalStats.totalGoalsSet}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                              / {globalStats.totalPossibleGoals}
                            </Typography>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{
                        borderRadius: '10px',
                        border: `1px solid ${alpha(globalStats.goalsRemaining > 0 ? theme.palette.warning.main : theme.palette.success.main, 0.18)}`,
                        backgroundColor: alpha(globalStats.goalsRemaining > 0 ? theme.palette.warning.main : theme.palette.success.main, 0.04),
                        boxShadow: 'none',
                      }}>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                            Goals Remaining
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: globalStats.goalsRemaining > 0 ? 'warning.dark' : 'success.main' }}>
                            {globalStats.goalsRemaining}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Completion bar */}
                  <Box sx={{ pt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Overall Completion</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: globalStats.completionPercentage === 100 ? 'success.main' : 'primary.main', lineHeight: 1 }}>
                        {globalStats.completionPercentage}%
                      </Typography>
        </Box>
                    <Box sx={{ width: '100%', height: 12, backgroundColor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%',
                        width: `${globalStats.completionPercentage}%`,
                        background: globalStats.completionPercentage === 100 ? theme.palette.success.main : 'linear-gradient(90deg, #1A3E5F 0%, #2E6DA4 100%)',
                        borderRadius: 2,
                        transition: 'width 0.4s ease',
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                      {globalStats.totalGoalsSet} of {globalStats.totalPossibleGoals} possible goals set
                    </Typography>
                  </Box>
                  </Box>
                </Grid>

                {/* Right: By Timeframe + By Resilience Factor */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                      By Timeframe
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <Box sx={{
                        flex: 1, p: 1.25, borderRadius: '8px',
                        backgroundColor: alpha(theme.palette.info.main, 0.07),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.25 }}>2030 Goals</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.dark', lineHeight: 1 }}>
                          {globalStats.goals5yrSet}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                          of {globalStats.totalPossible5yr ?? Math.floor(globalStats.totalPossibleGoals / 2)}
                        </Typography>
                      </Box>
                      <Box sx={{
                        flex: 1, p: 1.25, borderRadius: '8px',
                        backgroundColor: alpha(theme.palette.secondary.main, 0.07),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.25 }}>2035 Goals</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.dark', lineHeight: 1 }}>
                          {globalStats.goals10yrSet}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                          of {globalStats.totalPossible10yr ?? Math.floor(globalStats.totalPossibleGoals / 2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                      By Resilience Factor
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                      {Object.entries(globalStats.byFactor || {})
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([factorId, data]) => {
                          const factor = factors.find(f => f.id === parseInt(factorId));
                          return (
                            <Box key={factorId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5) }}>
                              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {factor?.name || `Factor ${factorId}`}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: '6px', backgroundColor: alpha(theme.palette.info.main, 0.1), minWidth: 52 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: 'info.dark', fontWeight: 600, lineHeight: 1.2 }}>2030</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: 'info.dark', fontWeight: 700, lineHeight: 1.2 }}>{data.goals5yr || 0}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: '6px', backgroundColor: alpha(theme.palette.secondary.main, 0.1), minWidth: 52 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: 'secondary.dark', fontWeight: 600, lineHeight: 1.2 }}>2035</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: 'secondary.dark', fontWeight: 700, lineHeight: 1.2 }}>{data.goals10yr || 0}</Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {/* Forecasting Statistics (Country-specific) */}
        {!loading && selectedCountry && sites.length > 0 && (
          <Paper
            sx={{
              p: 2.5,
              mb: embedded ? 2 : 4,
              width: '100%',
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Forecasting Progress
              </Typography>
            </Box>

            <Grid container spacing={2.5} alignItems="flex-start">
              {/* Left: 2×2 stat cards + completion bar */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                    Overview
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {/* Total Sites */}
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{
                      borderRadius: '10px',
                      border: `1px solid ${alpha('#1A3E5F', 0.18)}`,
                      backgroundColor: alpha('#1A3E5F', 0.04),
                      boxShadow: 'none',
                    }}>
                      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                          Total Sites
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: '#1A3E5F' }}>
                          {forecastStats.totalSites}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Sites With Goals Set */}
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{
                      borderRadius: '10px',
                      border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                      backgroundColor: alpha(theme.palette.success.main, 0.04),
                      boxShadow: 'none',
                    }}>
                      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                          Sites With Goals
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: 'success.main' }}>
                          {forecastStats.sitesWithGoals}
                          <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                            / {forecastStats.totalSites}
                          </Typography>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Goals Set */}
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{
                      borderRadius: '10px',
                      border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                      backgroundColor: alpha(theme.palette.info.main, 0.04),
                      boxShadow: 'none',
                    }}>
                      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                          Goals Set
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: 'info.dark' }}>
                          {forecastStats.totalGoalsSet}
                          <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                            / {forecastStats.totalPossibleGoals}
                          </Typography>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Goals Remaining */}
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{
                      borderRadius: '10px',
                      border: `1px solid ${alpha(forecastStats.goalsRemaining > 0 ? theme.palette.warning.main : theme.palette.success.main, 0.18)}`,
                      backgroundColor: alpha(forecastStats.goalsRemaining > 0 ? theme.palette.warning.main : theme.palette.success.main, 0.04),
                      boxShadow: 'none',
                    }}>
                      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>
                          Goals Remaining
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: forecastStats.goalsRemaining > 0 ? 'warning.dark' : 'success.main' }}>
                          {forecastStats.goalsRemaining}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Completion bar */}
                <Box sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Overall Completion</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: forecastStats.completionPercentage === 100 ? 'success.main' : 'primary.main', lineHeight: 1 }}>
                      {forecastStats.completionPercentage}%
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', height: 12, backgroundColor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%',
                      width: `${forecastStats.completionPercentage}%`,
                      background: forecastStats.completionPercentage === 100
                        ? theme.palette.success.main
                        : `linear-gradient(90deg, #1A3E5F 0%, #2E6DA4 100%)`,
                      borderRadius: 2,
                      transition: 'width 0.4s ease',
                    }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                    {forecastStats.totalGoalsSet} of {forecastStats.totalPossibleGoals} possible goals set
                  </Typography>
                </Box>
                </Box>
              </Grid>

              {/* Right: By Timeframe + By Resilience Factor */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {/* By Timeframe */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                    By Timeframe
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Box sx={{
                      flex: 1, p: 1.25, borderRadius: '8px',
                      backgroundColor: alpha(theme.palette.info.main, 0.07),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.25 }}>2030 Goals</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.dark', lineHeight: 1 }}>
                        {forecastStats.goals5yrSet}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                        of {forecastStats.totalPossible5yr}
                      </Typography>
                    </Box>
                    <Box sx={{
                      flex: 1, p: 1.25, borderRadius: '8px',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.07),
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.25 }}>2035 Goals</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.dark', lineHeight: 1 }}>
                        {forecastStats.goals10yrSet}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                        of {forecastStats.totalPossible10yr}
                      </Typography>
                    </Box>
                  </Box>

                  {/* By Resilience Factor */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem', mb: 1 }}>
                    By Resilience Factor
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                    {Object.entries(forecastStats.byFactor)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([factorId, data]) => {
                        const factor = factors.find(f => f.id === parseInt(factorId));
                        return (
                          <Box key={factorId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5) }}>
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {factor?.name || `Factor ${factorId}`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: '6px', backgroundColor: alpha(theme.palette.info.main, 0.1), minWidth: 52 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: 'info.dark', fontWeight: 600, lineHeight: 1.2 }}>2030</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: 'info.dark', fontWeight: 700, lineHeight: 1.2 }}>{data.goals5yr || 0}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: '6px', backgroundColor: alpha(theme.palette.secondary.main, 0.1), minWidth: 52 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: 'secondary.dark', fontWeight: 600, lineHeight: 1.2 }}>2035</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: 'secondary.dark', fontWeight: 700, lineHeight: 1.2 }}>{data.goals10yr || 0}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {!loading && hasFetched && sites.length === 0 && selectedCountry && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No sites found for the selected criteria
            </Typography>
          </Paper>
        )}

        {!loading && sites.map((site) => {
          const isExpanded = expandedCards[site.siteId] || false;
          const goalsSummary = getSiteGoalsSummary(site);
          
          return (
          <Paper 
            key={site.siteId}
            sx={{ 
              mb: embedded ? 3 : 4,
              borderRadius: 2,
              border: '1px solid #DEE2E6',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              overflow: 'hidden'
            }}
          >
            {/* Collapsible Header */}
            <Box
              onClick={() => toggleCardExpansion(site.siteId)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                cursor: 'pointer',
                backgroundColor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                borderBottom: isExpanded ? '1px solid #DEE2E6' : 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06)
                }
            }}
          >
            <Typography 
                variant={embedded ? 'h6' : 'h6'} 
              sx={{
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {site.siteName}
            </Typography>
            
              {/* Collapsed Summary - aligned right */}
              {!isExpanded && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', mr: 1 }}>
                  {/* Baseline (end of FY2025 snapshot) */}
                  {goalsSummary.avgBaseline !== null ? (
                    <Chip
                      label={`Baseline: ${goalsSummary.avgBaseline} (${goalsSummary.avgBaselinePercentage}%)`}
                      size="small"
                      sx={{
                        ...getScoreChipStyle(Math.round(goalsSummary.avgBaseline)),
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Chip
                      label="Baseline: —"
                      size="small"
                      sx={{
                        backgroundColor: '#BDBDBD',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  {/* Current Average Score */}
                  {goalsSummary.avgScore !== null ? (
                    <Chip
                      label={`Current: ${goalsSummary.avgScore} (${goalsSummary.avgPercentage}%)`}
                      size="small"
                      sx={{
                        ...getScoreChipStyle(Math.round(goalsSummary.avgScore)),
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Chip
                      label="Current: —"
                      size="small"
                      sx={{
                        backgroundColor: '#9E9E9E',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  {/* 5yr Goal Average */}
                  {goalsSummary.avg5yr !== null ? (
                    <Chip
                      label={`5yr: ${goalsSummary.avg5yr} (${goalsSummary.avg5yrPercentage}%)`}
                      size="small"
                      sx={{
                        ...getScoreChipStyle(Math.round(goalsSummary.avg5yr)),
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Chip
                      label="5yr: —"
                      size="small"
                      sx={{
                        backgroundColor: '#BDBDBD',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  {/* 10yr Goal Average */}
                  {goalsSummary.avg10yr !== null ? (
                    <Chip
                      label={`10yr: ${goalsSummary.avg10yr} (${goalsSummary.avg10yrPercentage}%)`}
                      size="small"
                      sx={{
                        ...getScoreChipStyle(Math.round(goalsSummary.avg10yr)),
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Chip
                      label="10yr: —"
                      size="small"
                      sx={{
                        backgroundColor: '#BDBDBD',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                </Box>
              )}
              
              <IconButton 
                size="small" 
                sx={{ 
                  color: 'primary.main',
                  transition: 'transform 0.2s ease'
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            {/* Collapsible Content */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, pt: 0 }}>
            <TableContainer sx={{ backgroundColor: '#FFFFFF' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', minWidth: 180 }}>Resilience Factor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 80 }}>Current</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 90 }}>5yr Goal</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 90 }}>10yr Goal</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', minWidth: 300 }}>Comments & Strategy</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 100 }}>AI Assist</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(factors || []).map((factor) => {
                  const existing = (site.factorScores || []).find(s => s.factorId === factor.id);
                  const score = existing || {
                    factorId: factor.id,
                    currentScore: null,
                    score5yrs: null,
                    score10yrs: null,
                    comments: '',
                    hasData: false,
                  };

                  // Factor is available for forecasting even if no current data exists
                  // hasData indicates whether there's underlying pipeline data
                  const hasCurrentData = score.hasData === true || (typeof score.currentScore === 'number' && score.currentScore !== null);
                  const currentScore = typeof score.currentScore === 'number' ? score.currentScore : null;
                  const rawScore5yrs = score.score5yrs;
                  const rawScore10yrs = score.score10yrs;

                  const has5yrGoal = isValidForecastScore(rawScore5yrs);
                  const has10yrGoal = isValidForecastScore(rawScore10yrs);

                  // What we display in the dropdowns (blank when no valid goals are set)
                  const displayScore5yrs = has5yrGoal ? rawScore5yrs : '';
                  const displayScore10yrs = has10yrGoal ? rawScore10yrs : '';

                  return (
                      <TableRow 
                        key={factor.id}
                      sx={{ 
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
                          backgroundColor: !hasCurrentData ? alpha(theme.palette.warning.light, 0.08) : 'transparent'
                      }}
                    >
                        <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                              variant="body2" 
                        sx={{
                                fontWeight: 600, 
                                fontSize: '0.875rem',
                            color: 'text.primary'
                        }}
                      >
                        {factor?.name || `Unknown Factor (ID: ${score.factorId})`}
                      </Typography>
                        {!hasCurrentData && (
                          <Chip 
                            label="No data" 
                            size="small" 
                            sx={{ 
                              height: 18, 
                              fontSize: '0.65rem', 
                              backgroundColor: alpha(theme.palette.warning.main, 0.15),
                              color: theme.palette.warning.dark,
                              fontWeight: 500
                            }} 
                          />
                        )}
                          </Box>
                          {(has5yrGoal || has10yrGoal) && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <Chip 
                                label={hasCurrentData ? currentScore : '—'} 
                                size="small" 
                                sx={{ 
                                  ...(hasCurrentData ? getScoreChipStyle(currentScore) : { backgroundColor: theme.palette.grey[300], color: theme.palette.grey[700] }), 
                                  height: 20, 
                                  fontSize: '0.7rem' 
                                }} 
                              />
                              {has5yrGoal && (
                                <>
                                  <ArrowForwardIcon sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
                                  <Chip label={rawScore5yrs} size="small" sx={{ ...getScoreChipStyle(rawScore5yrs), height: 20, fontSize: '0.7rem' }} />
                                </>
                              )}
                              {has10yrGoal && (
                                <>
                                  <ArrowForwardIcon sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
                                  <Chip label={rawScore10yrs} size="small" sx={{ ...getScoreChipStyle(rawScore10yrs), height: 20, fontSize: '0.7rem' }} />
                                </>
                              )}
                        </Box>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Chip 
                            label={hasCurrentData ? currentScore : 'No data'}
                            sx={{ 
                              ...(hasCurrentData ? getScoreChipStyle(currentScore) : { 
                                backgroundColor: alpha(theme.palette.warning.main, 0.15), 
                                color: theme.palette.warning.dark 
                              }), 
                              fontWeight: hasCurrentData ? 700 : 500,
                              fontSize: hasCurrentData ? '0.9rem' : '0.75rem',
                              minWidth: 45
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <TextField
                            select
                            value={displayScore5yrs}
                            onChange={(e) => handleScoreChange(site.siteId, score.factorId, 'score5yrs', parseInt(e.target.value))}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              width: 70,
                              '& .MuiSelect-select': { fontSize: '0.85rem', py: 0.5 }
                            }}
                            MenuProps={{ disableScrollLock: true }}
                          >
                            {SCORE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option} sx={{ fontSize: '0.85rem' }}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        <TableCell align="center">
                          <TextField
                            select
                            value={displayScore10yrs}
                            onChange={(e) => handleScoreChange(site.siteId, score.factorId, 'score10yrs', parseInt(e.target.value))}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              width: 70,
                              '& .MuiSelect-select': { fontSize: '0.85rem', py: 0.5 }
                            }}
                            MenuProps={{ disableScrollLock: true }}
                          >
                            {SCORE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option} sx={{ fontSize: '0.85rem' }}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={score.comments || ''}
                            onChange={(e) => handleScoreChange(site.siteId, score.factorId, 'comments', e.target.value)}
                            placeholder='Enter your strategic insights and action plans...'
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-input': { fontSize: '0.85rem' },
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'background.paper'
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title={!hasCurrentData ? 'AI recommendations require current score data' : 'Get AI-powered recommendations'}>
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={aiLoading[`${site.siteId}-${score.factorId}`] ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                                onClick={() => handleAIRecommendation(site.siteId, score.factorId, site.siteName, factor.name)}
                                disabled={!hasCurrentData || aiLoading[`${site.siteId}-${score.factorId}`]}
                                sx={{
                                  fontSize: '0.7rem',
                                  textTransform: 'none',
                                  px: 1,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.contrastText'
                                  }
                                }}
                              >
                                AI
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Save button for the entire site */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={() => handleSave(site)}
                sx={{
                  backgroundColor: '#2F6C75',
                  px: 3,
                  py: 1.25,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor: '#265A62',
                  }
                }}
              >
                Save Forecasting for {site.siteName}
              </Button>
            </Box>
          </Box>
        </Collapse>
          </Paper>
    );
  })}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* AI Recommendations Modal */}
      <Dialog 
        open={aiModalOpen} 
        onClose={() => setAiModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon color="primary" />
              AI Strategic Recommendations
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {currentAIContext.siteName} • {currentAIContext.factorName}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setAiModalOpen(false)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2.5, 
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              maxHeight: '60vh',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                maxHeight: '56vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1,
                scrollbarWidth: 'thin',
                scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 8 },
                '&::-webkit-scrollbar-track': { background: 'transparent' }
              }}
            >
              <Box sx={{ color: 'text.primary', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                {renderMarkdown(aiRecommendation)}
              </Box>
            </Box>
          </Paper>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              These are AI-generated suggestions based on current data. Please review, adapt, and combine with your local knowledge and expertise before using.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(aiRecommendation);
              setSnackbar({
                open: true,
                message: 'Recommendations copied to clipboard',
                severity: 'success'
              });
            }}
            sx={{ 
              textTransform: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderWidth: 1,
              transition: 'none',
              '&:hover': {
                transform: 'none !important',
                boxShadow: 'none',
                borderColor: 'divider',
                borderWidth: 1
              }
            }}
          >
            Copy to Clipboard
          </Button>
          <Button 
            variant="contained"
            onClick={handleCopyToComments}
            sx={{ 
              textTransform: 'none',
              backgroundColor: '#2F6C75',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: '#265A62',
              }
            }}
          >
            Use in Comments
          </Button>
        </DialogActions>
      </Dialog>

      {/* Country Summary Modal */}
      <Dialog 
        open={summaryModalOpen} 
        onClose={() => setSummaryModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SummarizeIcon color="primary" />
              National Impact Summary
            </Typography>
            {countrySummary && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {countrySummary.countryName} • {countrySummary.siteCount} sites
                </Typography>
                {countrySummary.cached && (
                  <Chip
                    icon={<CachedIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label="Cached"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.dark,
                      '& .MuiChip-icon': { color: theme.palette.success.dark }
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {countrySummary && !summaryLoading && (
              <Tooltip title="Generate a fresh summary">
                <IconButton 
                  onClick={() => handleCountrySummary(true)}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {countrySummary && !summaryLoading && (
              <Tooltip title="Download as PDF">
                <IconButton
                  onClick={() => downloadNationalSummaryPDF(countrySummary)}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton 
              onClick={() => setSummaryModalOpen(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {summaryLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={48} sx={{ color: '#2F6C75' }} />
              <Typography variant="h6" sx={{ mt: 3, fontWeight: 600, color: 'text.primary' }}>
                Generating National Summary
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Aggregating resilience scores across all sites
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Calculating 2030 and 2035 national goals
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Identifying priority areas and strengths
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Generating strategic recommendations...
                </Typography>
              </Box>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 3 }}>
                This may take 30–90 seconds
              </Typography>
            </Box>
          ) : countrySummary && (
            <>
              {/* Goals Overview Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      border: 1,
                      borderColor: alpha(theme.palette.warning.main, 0.3),
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                      Current
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                      {countrySummary.overallScores.current}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((countrySummary.overallScores.current / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      border: 1,
                      borderColor: alpha(theme.palette.info.main, 0.3),
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                      2030 Goal
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.dark }}>
                      {countrySummary.overallScores.goal2030}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((countrySummary.overallScores.goal2030 / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: 1,
                      borderColor: alpha(theme.palette.success.main, 0.3),
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                      2035 Goal
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.dark }}>
                      {countrySummary.overallScores.goal2035}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((countrySummary.overallScores.goal2035 / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* AI Summary */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  maxHeight: '55vh',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    maxHeight: '52vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 8 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' }
                  }}
                >
                  <Box sx={{ color: 'text.primary', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {renderMarkdown(countrySummary.summary)}
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Global Summary Modal */}
      <Dialog 
        open={globalSummaryModalOpen} 
        onClose={() => setGlobalSummaryModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SummarizeIcon color="primary" />
              Global Program Summary
            </Typography>
            {globalSummary && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {globalSummary.countryCount} countries • {globalSummary.siteCount} sites
                </Typography>
                {globalSummary.cached && (
                  <Chip 
                    icon={<CachedIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label="Cached" 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.65rem',
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      color: 'success.dark',
                      '& .MuiChip-icon': { color: 'success.dark' }
                    }} 
                  />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {globalSummary && (
              <Tooltip title="Regenerate summary">
                <IconButton 
                  onClick={() => handleGlobalSummary(true)} 
                  size="small"
                  disabled={globalSummaryLoading}
                  sx={{ color: 'text.secondary' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {globalSummary && !globalSummaryLoading && (
              <Tooltip title="Download as PDF">
                <IconButton
                  onClick={() => downloadGlobalSummaryPDF(globalSummary)}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={() => setGlobalSummaryModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {globalSummaryLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={48} sx={{ color: '#2F6C75' }} />
              <Typography variant="h6" sx={{ mt: 3, fontWeight: 600, color: 'text.primary' }}>
                Generating Global Summary
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Aggregating resilience scores across all countries
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Analyzing program-wide trends and patterns
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • Identifying cross-country learning opportunities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Generating strategic recommendations
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
                This may take 45–120 seconds...
              </Typography>
            </Box>
          ) : globalSummary && (
            <>
              {/* Overall Scores */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.warning.main, 0.08), border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.3) }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Current Average
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                      {globalSummary.overallScores.current}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((globalSummary.overallScores.current / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.info.main, 0.08), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.3) }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      2030 Goal
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.dark }}>
                      {globalSummary.overallScores.goal2030}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((globalSummary.overallScores.goal2030 / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.success.main, 0.08), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.3) }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      2035 Goal
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.dark }}>
                      {globalSummary.overallScores.goal2035}/4
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {((globalSummary.overallScores.goal2035 / 4) * 100).toFixed(0)}% resilience
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Country Breakdown */}
              {globalSummary.countryData && globalSummary.countryData.length > 0 && (
                <Paper sx={{ p: 2.5, mb: 3, backgroundColor: alpha(theme.palette.grey[100], 0.5), border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Resilience Scores by Country</Typography>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'divider' } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Country</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Sites</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Current</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>2030 Goal</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>2035 Goal</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Progress</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {globalSummary.countryData.map((country) => {
                          const has5yrGoal = country.avg5yrs !== null && country.avg5yrs !== undefined;
                          const has10yrGoal = country.avg10yrs !== null && country.avg10yrs !== undefined;
                          const hasImprovement = has10yrGoal && country.avgCurrent !== null && country.avg10yrs > country.avgCurrent;
                          const progressPercent = has10yrGoal && country.avgCurrent !== null && country.avgCurrent > 0
                            ? ((country.avg10yrs - country.avgCurrent) / country.avgCurrent) * 100 
                            : null;
                          
                          return (
                            <TableRow 
                              key={country.countryId}
                              sx={{ 
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
                                '&:last-child td': { borderBottom: 0 }
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {country.countryName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {country.siteCount}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {country.avgCurrent !== null && country.avgCurrent !== undefined ? (
                                  <Chip 
                                    label={country.avgCurrent.toFixed(1)} 
                                    size="small" 
                                    sx={{ 
                                      ...getScoreChipStyle(country.avgCurrent), 
                                      height: 24, 
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      minWidth: 45
                                    }} 
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {has5yrGoal ? (
                                  <Chip 
                                    label={country.avg5yrs.toFixed(1)} 
                                    size="small" 
                                    sx={{ 
                                      ...getScoreChipStyle(country.avg5yrs), 
                                      height: 24, 
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      minWidth: 45
                                    }} 
                                  />
                                ) : (
                                  <Chip 
                                    label="Not set" 
                                    size="small" 
                                    sx={{ 
                                      height: 24, 
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      minWidth: 45,
                                      backgroundColor: alpha(theme.palette.grey[300], 0.3),
                                      color: theme.palette.text.secondary,
                                      border: `1px dashed ${theme.palette.divider}`
                                    }} 
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {has10yrGoal ? (
                                  <Chip 
                                    label={country.avg10yrs.toFixed(1)} 
                                    size="small" 
                                    sx={{ 
                                      ...getScoreChipStyle(country.avg10yrs), 
                                      height: 24, 
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      minWidth: 45
                                    }} 
                                  />
                                ) : (
                                  <Chip 
                                    label="Not set" 
                                    size="small" 
                                    sx={{ 
                                      height: 24, 
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      minWidth: 45,
                                      backgroundColor: alpha(theme.palette.grey[300], 0.3),
                                      color: theme.palette.text.secondary,
                                      border: `1px dashed ${theme.palette.divider}`
                                    }} 
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {has10yrGoal && progressPercent !== null ? (
                                    <>
                                      <ArrowForwardIcon 
                                        sx={{ 
                                          fontSize: '1rem', 
                                          color: hasImprovement ? 'success.main' : 'text.secondary',
                                          transform: hasImprovement ? 'none' : 'rotate(180deg)'
                                        }} 
                                      />
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontWeight: 600,
                                          color: hasImprovement ? 'success.main' : 'text.secondary',
                                          fontSize: '0.7rem'
                                        }}
                                      >
                                        {hasImprovement ? `+${progressPercent.toFixed(0)}%` : '—'}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontWeight: 500,
                                        color: 'text.secondary',
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* AI Summary */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  maxHeight: '45vh',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    maxHeight: '42vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.2), borderRadius: 8 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' }
                  }}
                >
                  <Box sx={{ color: 'text.primary', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {renderMarkdown(globalSummary.summary)}
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) {
    return <Box sx={{ p: 0 }}>{content}</Box>;
  }

  return (
    <ContentLayout title="Impact Forecasting" noPaper>
      <Container maxWidth={false} sx={{ maxWidth: '1400px', px: { xs: 2, sm: 3, lg: 4 } }}>
      {content}
      </Container>
    </ContentLayout>
  );
};

export default ImpactForecasting; 